import type { FormFieldNestedListOptions, FormFieldNestedListValues } from '@ikegps/core-api';

/**
 * Constructs the entire NestedList options object to avoid individual sets which are expensive due to deep-cloning.
 *
 * This provides a wrapper around the internal nested list data, these helper classes will just have deep references inside the data.
 * Which also means they will generally not be == comparable, check the paths & value.
 */
export class FormModelFieldNestedListBuilder {
	group: FormModelFieldNestedListBuilderGroup;

	constructor(options?: FormFieldNestedListOptions) {
		if (options != null) {
			this.group = new FormModelFieldNestedListBuilderGroup(options);
		}
	}

	get options(): FormFieldNestedListOptions {
		if (this.group != null) {
			return this.group.options;
		}

		return null;
	}

	createGroup(title: string): FormModelFieldNestedListBuilderGroup {
		if (this.group != null && this.group.options.title.length > 0) {
			if (title === this.group.options.title) {
				return this.group;
			}
			throw new Error(`Cannot rename group from "${this.group.options.title}" to "${title}"`);
		}

		this.group = new FormModelFieldNestedListBuilderGroup({ title, values: [] });
		return this.group;
	}

	getGroup(path: number[]): FormModelFieldNestedListBuilderGroup {
		let opts = this.group.options;

		for (const pathIndex of path) {
			if (opts == null || opts.values == null || pathIndex >= opts.values.length) {
				return null;
			}
			opts = opts.values[pathIndex].options;
		}

		if (opts == null || opts.values == null) {
			return null;
		}

		return new FormModelFieldNestedListBuilderGroup(opts);
	}

	getValue(path: number[]): FormModelFieldNestedListBuilderValue {
		const group = this.getGroup(path.slice(0, path.length - 1));
		if (group == null) {
			return null;
		}

		return group.getValue(path[path.length - 1]);
	}
}

export class FormModelFieldNestedListBuilderGroup {
	options: FormFieldNestedListOptions;

	constructor(options?: FormFieldNestedListOptions) {
		if (options == null) {
			this.options = { title: '', values: [] };
		} else {
			this.options = options;
		}
	}

	get title(): string {
		return this.options.title;
	}

	get size(): number {
		return this.options.values.length;
	}

	setTitle(title: string) {
		this.options.title = title;
	}

	createValue(value: string, code?: string, color?: string, index?: number): FormModelFieldNestedListBuilderValue {
		const existingValue = this.options.values.find((v) => v.value === value);
		const existingColor = this.options.values.find((v) => v.color === color);
		if (existingValue && existingColor) {
			return new FormModelFieldNestedListBuilderValue(existingValue);
		}

		const newValue: FormFieldNestedListValues = { value, code, color };
		if (index != null) {
			this.options.values.splice(index, 0, newValue);
		} else {
			this.options.values.push(newValue);
		}

		return new FormModelFieldNestedListBuilderValue(newValue);
	}

	getValue(index: number): FormModelFieldNestedListBuilderValue {
		if (index >= 0 && index < this.options.values.length) {
			return new FormModelFieldNestedListBuilderValue(this.options.values[index]);
		}

		return null;
	}

	findValue(str: string): FormModelFieldNestedListBuilderValue {
		const index = this.options.values.findIndex((f) => f.value === str);
		return this.getValue(index);
	}

	deleteValue(index: number) {
		this.options.values.splice(index, 1);
	}
}

export class FormModelFieldNestedListBuilderValue extends FormModelFieldNestedListBuilder {
	listValues: FormFieldNestedListValues;

	constructor(listValues: FormFieldNestedListValues) {
		super();
		this.listValues = listValues;
	}

	get value(): string {
		return this.listValues.value;
	}

	get color(): string {
		return this.listValues.color;
	}

	setValue(value: string) {
		this.listValues.value = value;
	}

	setColor(color: string) {
		this.listValues.color = color;
	}

	override createGroup(title: string): FormModelFieldNestedListBuilderGroup {
		if (this.listValues.options != null && this.listValues.options.title === title) {
			return new FormModelFieldNestedListBuilderGroup(this.listValues.options);
		}

		const group = super.createGroup(title);
		this.listValues.options = group.options;

		return group;
	}
}
